import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { rewarmTopRoutes } from '@/utils/route-prefetch';

interface AdminAuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  userId: string | null;
  userEmail: string | null;
}

export const useAdminAuth = () => {
  const [state, setState] = useState<AdminAuthState>({
    isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
    userId: null,
    userEmail: null,
  });
  const { toast } = useToast();

  // Hard timeout for any single admin-role probe. The happy path runs in
  // ~500ms; an edge-function cold start or a dropped fetch can otherwise
  // leave the promise dangling forever, which strands the auth gate on
  // its "Verifying admin access…" spinner. 8s is comfortably above the
  // observed happy path and below the user-perceived "broken" threshold.
  const ADMIN_CHECK_TIMEOUT_MS = 8000;
  /** Number of probe attempts before falling back to the cached verdict. */
  const ADMIN_CHECK_RETRIES = 3;
  /** localStorage key prefix for the last *verified* admin verdict. */
  const ADMIN_VERDICT_KEY = 'yo:admin:verified';
  /**
   * How long a cached "admin=true" verdict is trusted without re-probing
   * the database on page load. Refresh tokens last ~30 days on Supabase,
   * but we re-verify daily so a revoked role takes effect within 24h.
   */
  const ADMIN_VERDICT_TTL_MS = 24 * 60 * 60 * 1000;

  const withTimeout = <T,>(p: Promise<T>, ms: number, label: string): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const t = setTimeout(
        () => reject(new Error(`${label} timed out after ${ms}ms`)),
        ms,
      );
      p.then(
        (v) => { clearTimeout(t); resolve(v); },
        (e) => { clearTimeout(t); reject(e); },
      );
    });

  type CachedVerdict = { isAdmin: boolean; at: number };
  const readCachedVerdict = (userId: string): CachedVerdict | null => {
    try {
      const raw =
        window.localStorage.getItem(`${ADMIN_VERDICT_KEY}:${userId}`) ??
        window.sessionStorage.getItem(`${ADMIN_VERDICT_KEY}:${userId}`);
      if (!raw) return null;
      // New format: JSON { isAdmin, at }. Legacy format: bare "true"/"false".
      if (raw === 'true') return { isAdmin: true, at: 0 };
      if (raw === 'false') return { isAdmin: false, at: 0 };
      const parsed = JSON.parse(raw);
      if (typeof parsed?.isAdmin === 'boolean' && typeof parsed?.at === 'number') {
        return parsed;
      }
      return null;
    } catch { return null; }
  };

  const isVerdictFresh = (v: CachedVerdict | null): boolean =>
    !!v && v.at > 0 && Date.now() - v.at < ADMIN_VERDICT_TTL_MS;

  const writeCachedVerdict = (userId: string, isAdmin: boolean) => {
    try {
      const payload = JSON.stringify({ isAdmin, at: Date.now() });
      window.localStorage.setItem(`${ADMIN_VERDICT_KEY}:${userId}`, payload);
      window.sessionStorage.setItem(`${ADMIN_VERDICT_KEY}:${userId}`, payload);
    } catch { /* ignore */ }
  };

  /**
   * Resolve the admin verdict with retries. A network failure / edge-function
   * timeout is NOT the same as "not an admin": on transient failures we retry
   * with backoff, then fall back to the last verified verdict for this user
   * (cached in sessionStorage) so a cold-start timeout on page load doesn't
   * bounce a legitimate admin to the login screen.
   */
  const checkAdminRole = useCallback(async (userId: string): Promise<boolean> => {
    // Primary path: direct RLS-protected query against user_roles. This is
    // fast and immune to edge-function cold starts / dev redeploy churn,
    // which were intermittently failing and bouncing admins to the login
    // form mid-session.
    try {
      const { data, error } = await withTimeout(
        Promise.resolve(
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId)
            .eq('role', 'admin')
            .maybeSingle(),
        ),
        ADMIN_CHECK_TIMEOUT_MS,
        'user_roles direct check',
      );
      if (!error) {
        const isAdmin = data?.role === 'admin';
        writeCachedVerdict(userId, isAdmin);
        return isAdmin;
      }
      console.warn('user_roles direct check errored; falling back to edge function:', error);
    } catch (e) {
      console.warn('user_roles direct check failed; falling back to edge function:', e);
    }
    for (let attempt = 1; attempt <= ADMIN_CHECK_RETRIES; attempt++) {
      try {
        const { data, error } = await withTimeout(
          supabase.functions.invoke('admin-roles', {
            body: { action: 'check_access' },
          }),
          ADMIN_CHECK_TIMEOUT_MS,
          'admin-roles check_access',
        );
        if (error) throw error;
        // Definitive server answer — cache it and return.
        const isAdmin = data?.isAdmin === true;
        writeCachedVerdict(userId, isAdmin);
        return isAdmin;
      } catch (error) {
        console.error(`Error checking admin role (attempt ${attempt}/${ADMIN_CHECK_RETRIES}):`, error);
        if (attempt < ADMIN_CHECK_RETRIES) {
          await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1)));
        }
      }
    }
    // All attempts failed (timeouts / network) — fall back to the last
    // verified verdict rather than treating the outage as a demotion.
    const cached = readCachedVerdict(userId);
    if (cached) {
      console.warn('admin-roles unreachable; using cached admin verdict:', cached.isAdmin);
      return cached.isAdmin;
    }
    return false;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const cached = readCachedVerdict(session.user.id);
        // Trust a fresh cached verdict — same "remember me" path as the
        // auth-state listener uses for INITIAL_SESSION.
        const isAdmin = isVerdictFresh(cached) && cached?.isAdmin
          ? true
          : await checkAdminRole(session.user.id);
        setState({
          isAuthenticated: true,
          isAdmin,
          isLoading: false,
          userId: session.user.id,
          userEmail: session.user.email || null,
        });
      } else {
        setState({
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
          userId: null,
          userEmail: null,
        });
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setState({
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
        userId: null,
        userEmail: null,
      });
    }
  }, [checkAdminRole]);

  useEffect(() => {
    // Watchdog: if the initial auth probe stalls (e.g. a flaky token refresh
    // never resolves), force `isLoading` to false after 4s so the gate
    // doesn't hang on the spinner forever. Subsequent listener events still
    // overwrite this fallback when they arrive.
    const watchdog = setTimeout(() => {
      setState((prev) =>
        prev.isLoading ? { ...prev, isLoading: false } : prev,
      );
    }, 4000);

    // Set up auth state listener BEFORE checking session.
    // Only re-run the admin role probe on the events that can actually
    // change the user's identity: INITIAL_SESSION and SIGNED_IN. Refresh
    // events (TOKEN_REFRESHED, USER_UPDATED) keep the same user and a
    // transient edge-function failure during those would otherwise
    // mis-flag the admin as non-admin and bounce them to the login screen.
    const REPROBE_EVENTS = new Set(['INITIAL_SESSION', 'SIGNED_IN']);
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Do NOT clear the watchdog up-front: if the admin probe below
      // stalls, the watchdog is our only fallback to flip isLoading false
      // and avoid an infinite "Verifying admin access…" spinner. Clear it
      // only after the awaited work resolves.
      if (session?.user) {
        if (REPROBE_EVENTS.has(event)) {
          const cached = readCachedVerdict(session.user.id);
          const fresh = isVerdictFresh(cached);

          // INITIAL_SESSION = page load with an existing refresh token.
          // If we verified this user as admin within the TTL, trust it
          // and skip the probe entirely — that's the "remember me" path.
          // Background revalidation still runs, but it can only *upgrade*
          // the verdict; a transient failure will never demote the user.
          if (event === 'INITIAL_SESSION' && fresh && cached?.isAdmin) {
            clearTimeout(watchdog);
            setState({
              isAuthenticated: true,
              isAdmin: true,
              isLoading: false,
              userId: session.user.id,
              userEmail: session.user.email || null,
            });
            rewarmTopRoutes();
            // Fire-and-forget revalidation. Only demote on an explicit
            // "false" verdict written by checkAdminRole; never demote on
            // a network/timeout fallback (those reuse the cached value
            // and would otherwise no-op anyway).
            void checkAdminRole(session.user.id).then((isAdmin) => {
              if (!isAdmin) {
                setState((prev) =>
                  prev.userId === session.user.id ? { ...prev, isAdmin: false } : prev,
                );
              }
            }).catch(() => undefined);
            return;
          }

          // Optimistic paint of the cached verdict (even if stale) so a
          // slow probe doesn't flash the login form for a known admin.
          if (cached?.isAdmin) {
            setState({
              isAuthenticated: true,
              isAdmin: true,
              isLoading: false,
              userId: session.user.id,
              userEmail: session.user.email || null,
            });
          }
          const isAdmin = await checkAdminRole(session.user.id);
          clearTimeout(watchdog);
          setState({
            isAuthenticated: true,
            isAdmin,
            isLoading: false,
            userId: session.user.id,
            userEmail: session.user.email || null,
          });
          rewarmTopRoutes();
        } else {
          // TOKEN_REFRESHED / USER_UPDATED / etc. — keep existing isAdmin,
          // just refresh identity fields so a missing one gets backfilled.
          clearTimeout(watchdog);
          setState((prev) => ({
            ...prev,
            isAuthenticated: true,
            isLoading: false,
            userId: session.user.id,
            userEmail: session.user.email || prev.userEmail,
          }));
        }
      } else {
        clearTimeout(watchdog);
        setState({
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
          userId: null,
          userEmail: null,
        });
        // Sign-out also resolves the gate; warm anonymous-accessible routes
        // for the next click.
        rewarmTopRoutes();
      }
    });

    // Check initial session
    checkAuth();

    return () => {
      clearTimeout(watchdog);
      subscription.unsubscribe();
    };
  }, [checkAuth, checkAdminRole]);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: "Access denied",
          description: error.message,
          variant: "destructive",
        });
        return { success: false, error: error.message };
      }

      if (data.user) {
        const isAdmin = await checkAdminRole(data.user.id);
        
        if (!isAdmin) {
          await supabase.auth.signOut();
          toast({
            title: "Access denied",
            description: "You do not have admin privileges.",
            variant: "destructive",
          });
          return { success: false, error: "Not an admin" };
        }

        // Log activity
        await logAdminActivity(data.user.id, 'login', 'Admin logged in');

        toast({
          title: "Access granted",
          description: `Welcome back, ${data.user.email}.`,
        });
        return { success: true };
      }

      return { success: false, error: "Unknown error" };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: "Sign in failed" };
    }
  };

  const signOut = async () => {
    try {
      if (state.userId) {
        await logAdminActivity(state.userId, 'logout', 'Admin logged out');
      }
      await supabase.auth.signOut();
      toast({
        title: "Logged out",
        description: "You have been logged out.",
      });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        toast({
          title: "Registration failed",
          description: error.message,
          variant: "destructive",
        });
        return { success: false, error: error.message };
      }

      if (data.user) {
        toast({
          title: "Registration successful",
          description: "Please check your email to verify your account.",
        });
        return { success: true, user: data.user };
      }

      return { success: false, error: "Unknown error" };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: "Sign up failed" };
    }
  };

  return {
    ...state,
    signIn,
    signOut,
    signUp,
    checkAuth,
  };
};

// Helper function to log admin activity
export const logAdminActivity = async (
  userId: string,
  action: string,
  details: string
) => {
  try {
    await supabase.from('admin_activity_log').insert({
      user_id: userId,
      action,
      details,
    });
  } catch (error) {
    console.error('Error logging activity:', error);
  }
};

// Function to get activity log
export const getAdminActivityLog = async () => {
  try {
    const { data, error } = await supabase
      .from('admin_activity_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      console.error('Error fetching activity log:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching activity log:', error);
    return [];
  }
};
